import * as api from "../client";
import type { ListResponse, PageType, StatusUpdatePayload, TeamPage } from "./types";

const BASE = "/v1/profiles/team-pages";

// ── Team-page child-collection shapes (Phase 4 alignment) ───────────────────
// These mirror the profiles-api input schemas exactly (see
// `services/profiles-api/src/schemas/teamPages.ts` + `controllers/team-pages`).
// They live here (the team service) rather than the shared `types.ts` barrel so
// the Phase-4 work doesn't widen the shared cross-form surface.

export type TeamTestimonial = {
  clientName: string;
  clientCompany?: string;
  testimonial: string;
  sortOrder: number;
};

/**
 * Award shape aligned to the canonical account-ui / profiles-api
 * `TeamAwardInputSchema` (`{ logoHash?, logoFilename?, title, shortDescription?,
 * sortOrder? }`). Replaces the legacy staff `{ awardTitle, description }` shape.
 */
export type TeamAward = {
  title: string;
  logoHash: string | null;
  logoFilename: string | null;
  shortDescription: string;
  sortOrder: number;
};

export type TeamDeal = { content: string; sortOrder: number };

/**
 * Whitepaper shape aligned to `RawTeamWhitepaperSchema`
 * (`{ documentHash?, documentFilename?, title?, shortDescription?, sortOrder? }`).
 * `title` is now collected (required client-side) and the document is stored as
 * an inline `{ hash, filename }` blob reference.
 */
export type TeamWhitepaper = {
  title: string;
  documentHash: string | null;
  documentFilename: string | null;
  shortDescription: string;
  sortOrder: number;
};

/**
 * Member shape aligned to `TeamMemberInputSchema` (`{ lawyerProfileId, sortOrder? }`).
 * The legacy staff `role` field has no backend column and is dropped.
 */
export type TeamMember = { lawyerProfileId: string; sortOrder: number };

export type TeamPageFilters = {
  companyId?: string;
  pageType?: PageType;
  status?: string;
  /** Free-text search; forwarded to the API as `search`. */
  q?: string;
  /**
   * Region filter (§3.4): `regionKind` + `regionId` (integer) together or
   * neither. `regionId` is the integer `api_region_country_v2` id.
   */
  regionKind?: "region" | "country";
  regionId?: string;
  limit?: number;
  offset?: number;
};

export const teamPagesService = {
  async list(filters: TeamPageFilters = {}): Promise<ListResponse<TeamPage>> {
    const params = new URLSearchParams();
    if (filters.companyId) params.append("companyId", filters.companyId);
    if (filters.pageType) params.append("pageType", filters.pageType);
    if (filters.status) params.append("status", filters.status);
    if (filters.q) params.append("search", filters.q);
    if (filters.regionKind && filters.regionId) {
      params.append("regionKind", filters.regionKind);
      params.append("regionId", filters.regionId);
    }
    if (filters.limit !== undefined) params.append("limit", String(filters.limit));
    if (filters.offset !== undefined) params.append("offset", String(filters.offset));
    const query = params.toString() ? `?${params}` : "";
    const raw = await api.get<{
      teamPages: TeamPage[];
      pagination: { total: number; limit: number; offset: number };
    }>(`${BASE}${query}`);
    return {
      data: raw.teamPages ?? [],
      total: raw.pagination?.total ?? 0,
      limit: raw.pagination?.limit ?? 20,
      offset: raw.pagination?.offset ?? 0,
    };
  },

  async get(id: string): Promise<TeamPage> {
    const raw = await api.get<{ teamPage: TeamPage } | TeamPage>(`${BASE}/${id}`);
    return (raw as { teamPage?: TeamPage }).teamPage ?? (raw as TeamPage);
  },

  async updateStatus(id: string, payload: StatusUpdatePayload): Promise<TeamPage> {
    const raw = await api.patch<{ teamPage: TeamPage } | TeamPage>(`${BASE}/${id}/status`, payload);
    return (raw as { teamPage?: TeamPage }).teamPage ?? (raw as TeamPage);
  },
};
